import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, dateTimeFmt, money, phoneFmt } from '../api';
import { useApi } from '../useApi';
import { Empty, ErrorBox, Loading, useToast } from '../ui';

type Conv = {
  id: string;
  channel: string;
  externalId: string;
  unread: number;
  lastAt: string;
  client: { id: string; name: string; code: string; phone: string; cachedBalanceKop: string } | null;
  messages: Array<{ id: string; text: string; direction: string; createdAt: string; authorName: string | null }>;
};

const CHANNEL_RU: Record<string, string> = {
  TELEGRAM: 'Telegram',
  WHATSAPP: 'WhatsApp',
  MAX: 'MAX',
  SMS: 'SMS',
  WEB: 'Сайт',
  PHONE: 'Звонок',
};

/**
 * Единое окно переписки. Смысл экрана — оператор не переключается между
 * пятью приложениями: всё, что написали клиенты в любом канале, лежит здесь,
 * и ответ уходит тем же каналом, которым пришёл вопрос.
 */
export function Inbox() {
  const [openId, setOpenId] = useState<string | null>(null);
  const list = useApi<Conv[]>('/conversations?limit=100');
  const thread = useApi<Conv>(openId ? `/conversations/${openId}` : null);
  const [text, setText] = useState('');
  const toast = useToast();

  const send = async () => {
    if (!openId || !text.trim()) return;
    const res = await api<{ channel: string; ok: boolean }>(`/conversations/${openId}/reply`, {
      body: { text },
    });
    toast.show(res.ok ? `Отправлено (${CHANNEL_RU[res.channel.toUpperCase()] ?? res.channel})` : 'Не ушло');
    setText('');
    await thread.reload();
    await list.reload();
  };

  return (
    <>
      <h1>Сообщения</h1>
      <div className="row" style={{ alignItems: 'stretch' }}>
        <div className="card" style={{ flex: '0 0 340px', maxHeight: '75vh', overflowY: 'auto' }}>
          {list.loading ? (
            <Loading />
          ) : list.error ? (
            <ErrorBox error={list.error} />
          ) : !list.data?.length ? (
            <Empty>Входящих нет</Empty>
          ) : (
            list.data.map((c) => (
              <button
                key={c.id}
                onClick={() => setOpenId(c.id)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  border: 'none',
                  borderBottom: '1px solid #eef2f6',
                  borderRadius: 0,
                  padding: '10px 6px',
                  background: openId === c.id ? 'var(--blue-light)' : 'transparent',
                }}
              >
                <div className="spread">
                  <b>{c.client?.name ?? phoneFmt(c.externalId)}</b>
                  <span className="badge">{CHANNEL_RU[c.channel] ?? c.channel}</span>
                </div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {c.messages[0]?.text?.slice(0, 60) ?? ''}
                </div>
                <div className="spread">
                  <span className="muted" style={{ fontSize: 11 }}>{dateTimeFmt(c.lastAt)}</span>
                  {c.unread > 0 ? <span className="badge blue">{c.unread}</span> : null}
                </div>
              </button>
            ))
          )}
        </div>

        <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', maxHeight: '75vh' }}>
          {!openId ? (
            <Empty>Выберите диалог слева</Empty>
          ) : thread.loading ? (
            <Loading />
          ) : thread.data ? (
            <>
              <div className="spread" style={{ borderBottom: '1px solid var(--line)', paddingBottom: 10 }}>
                <div>
                  <b>{thread.data.client?.name ?? phoneFmt(thread.data.externalId)}</b>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {CHANNEL_RU[thread.data.channel] ?? thread.data.channel}
                    {thread.data.client ? ` · баланс ${money(thread.data.client.cachedBalanceKop)}` : ' · не в базе'}
                  </div>
                </div>
                {thread.data.client ? (
                  <Link className="btn" to={`/clients/${thread.data.client.id}`}>
                    Карточка
                  </Link>
                ) : null}
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: '12px 0' }}>
                {thread.data.messages.map((m) => (
                  <div
                    key={m.id}
                    style={{
                      margin: '6px 0',
                      display: 'flex',
                      justifyContent: m.direction === 'in' ? 'flex-start' : 'flex-end',
                    }}
                  >
                    <div
                      style={{
                        maxWidth: '70%',
                        padding: '8px 12px',
                        borderRadius: 10,
                        background: m.direction === 'in' ? '#eef2f6' : 'var(--blue-light)',
                      }}
                    >
                      {m.text}
                      <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                        {dateTimeFmt(m.createdAt)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="row" style={{ borderTop: '1px solid var(--line)', paddingTop: 10 }}>
                <input
                  style={{ flex: 1 }}
                  value={text}
                  placeholder="Ответ уйдёт тем же каналом"
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void send();
                  }}
                />
                <button className="primary" onClick={() => void send()}>
                  Отправить
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
      {toast.node}
    </>
  );
}
